import { z } from "zod"

export const ProductSummarySchema = z.object({
    title: z.string(),
    handle: z.string(),
    categories: z.array(z.string()),
    thumbnail: z.string().nullish(),
    tags: z.array(z.string()),
    sizes: z.array(
        z.object({
            dimensions: z.object({
                width: z.number(),
                length: z.number(),
            }),
            original_price: z.number().nullish(),
            calculated_price: z.number().nullish(),
        }),
    ),
    prices: z.array(
        z.object({
            calculated_amount: z.number(),
            original_amount: z.number(),
            options: z.record(z.string(), z.string()),
        }),
    ),
})

export type ProductSummary = z.infer<typeof ProductSummarySchema>

export const ProductsFiltersSchema = z.object({
    categories: z.record(
        z.string(),
        z.object({
            productIds: z.array(z.string()),
            count: z.number(),
        }),
    ),
    tags: z.record(
        z.string(),
        z.object({
            productIds: z.array(z.string()),
            count: z.number(),
        }),
    ),
    priceRange: z.object({
        min: z.number(),
        max: z.number(),
    }),
    sizes: z.record(
        z.string(),
        z.object({
            productIds: z.array(z.string()),
            count: z.number(),
        }),
    ),
})

export type ProductsFilters = z.infer<typeof ProductsFiltersSchema>

const ProductSearchDataResponseSchema = z.object({
    products: z.record(z.string(), ProductSummarySchema),
    filters: ProductsFiltersSchema,
})

export type ProductsSearchDataResponse = z.infer<
    typeof ProductSearchDataResponseSchema
>

const RelatedProductsResponseSchema = z.array(ProductSummarySchema)

export type RelatedProductsResponse = z.infer<
    typeof RelatedProductsResponseSchema
>

export const ProductDisplayDataSchema = z.object({
    variants: z.array(
        z.object({
            id: z.string(),
            title: z.string().nullable(),
            options: z.array(
                z.object({
                    option_id: z.string(),
                    value: z.string(),
                    option: z.object({
                        id: z.string(),
                        title: z.string(),
                    }),
                }),
            ),
            calculated_price: z.object({
                calculated_amount: z.number().nullable(),
                original_amount: z.number().nullable(),
            }),
        }),
    ),
    options: z.array(
        z.object({
            id: z.string(),
            title: z.string(),
            values: z.array(
                z.object({
                    id: z.string(),
                    value: z.string(),
                }),
            ),
        }),
    ),
    title: z.string().nullable(),
    description: z.string().nullable(),
    thumbnail: z.string().nullable(),
    handle: z.string().nullable(),
    images: z.array(z.object({ url: z.string() })).nullable(),
    categories: z.array(z.object({ id: z.string() })),
    tags: z.array(z.object({ id: z.string() })),
})

export type ProductDisplayData = z.infer<typeof ProductDisplayDataSchema>

export const ReviewSchema = z.object({
    id: z.string(),
    name: z.string(),
    text: z.string(),
    image_url: z.string().nullish(),
    rating: z.number(),
})

export type Review = z.infer<typeof ReviewSchema>

const GetStoreReviewsRequestSchema = z.object({
    limit: z.number().nullish(),
    offset: z.number().nullish(),
    order: z.string().nullish(),
})

export type GetStoreReviewsRequest = z.infer<
    typeof GetStoreReviewsRequestSchema
>

export const StoreApi = {
    CUSTOM_ORDERS: {
        route: "/store/custom-orders",
        requestSchema: z.object({
            customer_name: z.string().nullish(),
            customer_phone: z.string(),
            product_variant_ids: z.array(z.string()),
        }),
        responseSchema: z.unknown(),
    },
    CALL_REQUEST: {
        route: "/store/call-request",
        requestSchema: z.object({
            customer_name: z.string().nullish(),
            customer_phone: z.string(),
        }),
        responseSchema: z.unknown(),
    },
    QUIZ_RESULT: {
        route: "/store/quiz-result",
        requestSchema: z.object({
            customer_name: z.string().nullish(),
            customer_phone: z.string(),
            customer_address: z.string().nullish(),
            quiz_results_map: z.record(z.string(), z.string()),
        }),
        responseSchema: z.unknown(),
    },
    PRODUCTS_SEARCH_DATA: {
        route: "/store/product/search-data",
        requestSchema: z.object({}),
        responseSchema: ProductSearchDataResponseSchema,
    },
    RELATED_PRODUCTS: {
        route: "/store/product/related-products",
        requestSchema: z.object({
            categories_ids: z.array(z.string()).nullish(),
            tag_ids: z.array(z.string()).nullish(),
        }),
        responseSchema: RelatedProductsResponseSchema,
    },
    PRODUCT_DISPLAY_DATA: {
        route: "/store/product/display-data",
        requestSchema: z.object({
            handle: z.string(),
        }),
        responseSchema: ProductDisplayDataSchema,
    },
    REVIEWS: {
        route: "/store/reviews",
        requestSchema: GetStoreReviewsRequestSchema,
        responseSchema: z.object({
            reviews: z.array(ReviewSchema),
            count: z.number().nullish(),
            limit: z.number().nullish(),
            offset: z.number().nullish(),
        }),
    },
} as const satisfies Record<
    PropertyKey,
    {
        route: string
        requestSchema: z.ZodSchema
        responseSchema: z.ZodSchema
    }
>

export type PostStoreCreateCustomOrderRequest = z.infer<
    typeof StoreApi.CUSTOM_ORDERS.requestSchema
>

export type PostStoreSendCallRequestRequest = z.infer<
    typeof StoreApi.CALL_REQUEST.requestSchema
>

export type PostStoreSendQuizResultRequest = z.infer<
    typeof StoreApi.QUIZ_RESULT.requestSchema
>

export type GetStoreProductsSearchDataRequest = z.infer<
    typeof StoreApi.PRODUCTS_SEARCH_DATA.requestSchema
>

export type GetStoreRelatedProductsRequest = z.infer<
    typeof StoreApi.RELATED_PRODUCTS.requestSchema
>

export type GetStoreProductDisplayDataRequest = z.infer<
    typeof StoreApi.PRODUCT_DISPLAY_DATA.requestSchema
>

export type GetStoreReviewsResponse = z.infer<
    typeof StoreApi.REVIEWS.responseSchema
>
